import { useState, useEffect } from 'react';
import { history } from 'umi';
import { useIntl } from 'react-intl';
import { Row, Col } from 'antd';
import { emailReg, nameReg } from '@/utils/regexp';
import { Form, message } from 'iglooform';
import throttle from 'lodash/throttle';
import './edit-profile.less';
import { BackButton } from '@/components/back-button/back-button';
import { getAgencyInfo } from '../service';
import moment from 'moment';
import SkeletonLoading from '@/components/skeleton-loading';

const EditProfile = () => {
  const { formatMessage } = useIntl();
  const form = Form.useForm();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [state, setState] = useState<number>();

  const handleGetAgencyInfo = throttle(
    async () => {
      try {
        setLoading(true);
        getAgencyInfo()
          .then((res) => {
            if (res) {
              const { userInfo } = res;
              setState(userInfo.status);
              form.setFields([
                { name: 'first_name', value: userInfo.first_name },
                { name: 'last_name', value: userInfo.last_name },
                { name: 'date_of_birth', value: userInfo.date_of_birth },
                { name: 'id_issued_date', value: userInfo.id_issued_date },
                { name: 'national_id', value: userInfo.national_id },
                { name: 'email', value: userInfo.email },
                {
                  name: ['phone_number', 'phoneNumber'],
                  value: userInfo.phone_number,
                },
                { name: 'address', value: userInfo.address },
                { name: 'province', value: userInfo.province },
                { name: 'city', value: userInfo.city },
                { name: 'postal_code', value: userInfo.postal_code },
              ]);
            }
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        setLoading(false);
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  const handleEditAgencyInfo = throttle(
    async (value) => {
      try {
        setLoading(true);
        const params = value;

        editAgencyInfo(params)
          .then((res) => {
            if (res) {
              message.success(
                formatMessage({ id: 'Edit Profile Successful!' }),
              );
              history.push('/me/profile');
            }
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        setLoading(false);
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  useEffect(() => {
    handleGetAgencyInfo();
  }, []);

  return (
    <div className="editProfileLayout">
      <BackButton handleClick={() => history.goBack()} />
      {!isLoading ? (
        <Row style={{ marginBottom: '100px', marginTop: '40px' }}>
          <Col>
            <Form
              form={form}
              config={{
                type: 'Login',
                elements: [
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'Last Name',
                      defaultMessage: 'Last Name',
                    }),
                    name: 'last_name',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                    rules: [
                      {
                        validator: (_: any, value: string) => {
                          if (!!value && !nameReg.test(value)) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'This is not a valid Last Name.',
                                }),
                              ),
                            );
                          } else if (!!value && value.length > 80) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'The max length of Last Name should be 80 characters',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'First Name',
                      defaultMessage: 'First Name',
                    }),
                    name: 'first_name',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                    rules: [
                      {
                        validator: (_: any, value: string) => {
                          if (!!value && !nameReg.test(value)) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'This is not a valid First Name.',
                                }),
                              ),
                            );
                          } else if (!!value && value.length > 80) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'The max length of First Name should be 80 characters',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    type: 'DatePicker',
                    label: formatMessage({
                      id: 'Date of Birth',
                      defaultMessage: 'Date of Birth',
                    }),
                    name: 'date_of_birth',
                    halfRow: true,
                    required: true,
                    initialValue: '',
                    rules: [
                      {
                        validator: (rule: any, value: number) => {
                          const nowTime = moment(new Date()).format(
                            'YYYY-MM-DD',
                          );
                          const birthday = moment(value).format('YYYY-MM-DD');
                          if (
                            !!value &&
                            moment(nowTime)
                              .add(7, 'hours')
                              .isBefore(moment(birthday).add(18, 'years'))
                          ) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'You need to be at least 18 years old',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'ID Number',
                      defaultMessage: 'ID Number',
                    }),
                    name: 'national_id',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                    rules: [{}],
                  },
                  {
                    type: 'DatePicker',
                    label: formatMessage({
                      id: 'ID Issued Date',
                      defaultMessage: 'ID Issued Date',
                    }),
                    name: 'id_issued_date',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                    rules: [],
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'Email',
                      defaultMessage: 'Email',
                    }),
                    name: 'email',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                    disabled: true,
                    rules: [
                      {
                        validator: (rule: any, value: string) => {
                          if (!!value && !emailReg.test(value)) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'Invalid email format',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    type: 'PhoneNumber',
                    label: formatMessage({
                      id: 'Mobile Number',
                      defaultMessage: 'Mobile Number',
                    }),
                    name: 'phone_number',
                    fullRow: false,
                    required: true,
                    disabled: true,
                    initialValue: '',
                    areaCode: '+84',
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'Address',
                      defaultMessage: 'Address',
                    }),
                    name: 'address',
                    fullRow: false,
                    required: false,
                    initialValue: '',
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'Province',
                      defaultMessage: 'Province',
                    }),
                    name: 'province',
                    fullRow: false,
                    required: false,
                    initialValue: '',
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'City',
                      defaultMessage: 'City',
                    }),
                    name: 'city',
                    fullRow: false,
                    required: false,
                    initialValue: '',
                  },
                ],
              }}
              onSubmit={(value) => handleEditAgencyInfo(value)}
              locales={{
                submitText: formatMessage({
                  id: 'Save',
                  defaultMessage: 'Save',
                }),
                validateMessages: {
                  required: formatMessage({
                    id: 'This is required',
                  }),
                },
              }}
            />
          </Col>
        </Row>
      ) : (
        <div style={{ marginTop: '45px' }}>
          <SkeletonLoading />
        </div>
      )}
    </div>
  );
};

export default EditProfile;
